import React, { createContext, useCallback, useContext } from 'react';
import { useAuth } from './AuthContextZustand';
import { useToast } from '../components/UI';
import {
  useSpotlightTour,
  type UseSpotlightTourResult,
} from '../hooks/onboarding/useSpotlightTour';
import { FieldGuideTooltip, SpotlightOverlay, TourTooltip } from '../components/onboarding';

type SpotlightTourContextValue = UseSpotlightTourResult;

const SpotlightTourContext = createContext<SpotlightTourContextValue | null>(null);

function SpotlightTourLayer({ tour }: { tour: UseSpotlightTourResult }) {
  if (!tour.currentStep) {
    return null;
  }

  const skipHandler = tour.currentStep.blocking ? undefined : () => void tour.handleSkipStep();

  if (tour.tourPhase === 'intro') {
    return (
      <SpotlightOverlay spotlightRect={null}>
        <TourTooltip
          step={tour.currentStep}
          stepIndex={tour.currentStepIndex}
          totalSteps={tour.totalSteps}
          targetRect={null}
          onGoToAction={() => void tour.handleGoToAction()}
          onSkip={skipHandler}
          onClose={() => void tour.handleCloseTour()}
          isClosing={tour.isClosingTour}
        />
      </SpotlightOverlay>
    );
  }

  if (!tour.currentTarget) {
    return null;
  }

  return (
    <>
      <SpotlightOverlay
        spotlightRect={tour.spotlightRect}
        clipBounds={tour.clipBounds}
        aboveModal={tour.isModalTarget}
      />
      <FieldGuideTooltip
        target={tour.currentTarget}
        targetIndex={tour.guidedTargetIndex}
        targetCount={tour.guidedTargetCount}
        targetRect={tour.spotlightRect}
        canGoNext={tour.canGoNextTarget}
        onNext={tour.handleNextTarget}
        onAdvanceToNextStep={() => void tour.handleAdvanceToNextStep()}
        onClose={() => void tour.handleCloseTour()}
        isClosing={tour.isClosingTour}
        aboveModal={tour.isModalTarget}
      />
    </>
  );
}

export const SpotlightTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { addToast } = useToast();

  const handleTourComplete = useCallback(() => {
    addToast({
      type: 'success',
      title: 'Parabéns!',
      message: 'Você concluiu todos os passos de configuração.',
      duration: 6000,
    });
  }, [addToast]);

  const handleTourError = useCallback(
    (message: string) => {
      addToast({
        type: 'error',
        title: 'Tour de configuração',
        message,
        duration: 5000,
      });
    },
    [addToast]
  );

  const tour = useSpotlightTour(handleTourComplete, handleTourError);

  const contextValue: SpotlightTourContextValue | null =
    !isLoading && isAuthenticated && tour.isActive ? tour : null;

  return (
    <SpotlightTourContext.Provider value={contextValue}>
      {children}
      {!isLoading && isAuthenticated && tour.isActive ? <SpotlightTourLayer tour={tour} /> : null}
    </SpotlightTourContext.Provider>
  );
};

export function useSpotlightTourContext(): SpotlightTourContextValue {
  const context = useContext(SpotlightTourContext);
  if (!context) {
    throw new Error('useSpotlightTourContext deve ser usado dentro de SpotlightTourProvider');
  }
  return context;
}

export function useOptionalSpotlightTourContext(): SpotlightTourContextValue | null {
  return useContext(SpotlightTourContext);
}
